import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { atomAllLocationsQuery } from "@/modules/location/atomAllLocationsQuery.ts";
import { ipAddressLink } from "@/modules/network/links.ts";
import {
    raiseBackgroundErrorToast,
    raiseRequestToast,
    type RequestToastOptions,
    type Toast,
} from "@/modules/notifications/toast.tsx";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { produceQueryData } from "@/utils/query/produceQueryData.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions, runMutation } from "@/utils/query/runMutation.ts";
import { type QueryClient, type QueryKey, queryOptions } from "@tanstack/react-query";
import type {
    IpAddress,
    IpAddressAssignBody,
    IpAddressCreateBody,
    IpAddressModifyBody,
} from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries, mergeMaps } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";

//#region Basic Types/Contracts
export interface Params {
    location: string;
}

export interface IpAddressWithType extends IpAddress, Params {
    $type: "ip_address";
}

export type Response = Map<IpAddress["uuid"], IpAddressWithType>;

const emptyResponse: Response = new Map();

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("ip_address") };

//#endregion Basic Types/Contracts

//#region Helper functions

// NB: Need explicit return type to avoid TypeScript error for `UseQueryOptions` below.
export function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["ip_address/list"] : ["ip_address/list", params.location];
}

function ipFromData(data: IpAddress, location: string): IpAddressWithType {
    return { ...data, $type: "ip_address", location };
}

//#endregion Helper functions

//#region Queries

function getSingleQuery(apiClient: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location } = params;
            const response = getResponseData(
                await apiClient.GET("/{location}/network/ip_addresses", {
                    signal,
                    params: { path: { location } },
                }),
            );
            return mapFromEntries(response, (ip) => [ip.uuid, ipFromData(ip, location)]);
        },
    });
}

export const ipQueryAtom = atomFamily((location: string) => atomFromStandardQueryOptions(getSingleQuery, { location }));

export const allIpQueryAtom = atomAllLocationsQuery(ipQueryAtom, (results) => mergeMaps(results));

//#endregion Queries

//#region Mutations

/** Two-stage mutation that first creates the IP address and then assigns it to a resource */
export function createFullIpAddressMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        async mutationFn({
            body,
            location,
            assignBody,
        }: {
            body: IpAddressCreateBody;
            location: string;
            assignBody: IpAddressAssignBody | undefined;
        }) {
            const result = await runMutation(queryClient, ipAddressCreateMutation(api, queryClient), {
                body,
                location,
            });

            let assignResult = null;
            if (assignBody !== undefined) {
                try {
                    assignResult = await runMutation(queryClient, assignIpAddressMutation(api, queryClient), {
                        body: assignBody,
                        location,
                        address: result.address,
                        mutationToastOptions: { silent: true },
                    });
                } catch (e) {
                    // Not being able to assign IP address is not a critical error, still return the created IP address
                    raiseBackgroundErrorToast(e, "IP assign failed");
                }
            }

            return {
                result: ipFromData(result, location),
                assignResult: assignResult && ipFromData(assignResult, location),
            };
        },
    });
}

function ipAddressCreateMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["ip_address/create"],
        async mutationFn({ body, location }: { body: IpAddressCreateBody; location: string }) {
            const result = getResponseData(
                await api.POST("/{location}/network/ip_addresses", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
            return ipFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "IP Address created",
                error: "Error creating IP Address",
                successType: "success",
                viewLink: res ? ipAddressLink(res) : undefined,
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function modifyIpAddressMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["ip_address/modify"],
        async mutationFn({
            body,
            location,
            address,
        }: {
            body: IpAddressModifyBody;
            location: string;
            address: string;
        }) {
            const result = getResponseData(
                await api.PATCH("/{location}/network/ip_addresses/{address}", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location, address } },
                }),
            );
            return ipFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "IP Address updated",
                error: "Error updating IP Address",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function assignIpAddressMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["ip_address/assign"],
        async mutationFn({
            body,
            location,
            address,
        }: {
            body: IpAddressAssignBody;
            location: string;
            address: string;
            mutationToastOptions?: Partial<RequestToastOptions>;
        }) {
            const result = getResponseData(
                await api.POST("/{location}/network/ip_addresses/{address}/assign", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location, address } },
                }),
            );
            return ipFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(_res, err, { location, mutationToastOptions }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "IP Address assigned to resource",
                error: "Error assigning IP Address",
                ...mutationToastOptions,
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function unassignIpAddressMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["ip_address/unassign"],
        async mutationFn({ location, address }: { location: string; address: string }) {
            const result = getResponseData(
                await api.POST("/{location}/network/ip_addresses/{address}/unassign", {
                    params: { path: { location, address } },
                }),
            );
            return ipFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "IP Address unassigned",
                error: "Error unassigning IP Address",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function deleteIpAddressMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["ip_address/delete"],
        async mutationFn({ location, address }: { location: string; address: string }) {
            return getResponseData(
                await api.DELETE("/{location}/network/ip_addresses/{address}", {
                    params: { path: { location, address } },
                }),
            );
        },
        async onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "IP Address deleted",
                error: "Error deleting IP Address",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) });
        },
    });
}

//#endregion Mutations
